import { useState, useEffect } from 'react';
import { getDoctors } from '@/lib/api';
import { displayAge } from '@/lib/patientAge';
import { createAdmission, getWards, getBeds, type Ward, type Bed } from '@/lib/ipdApi';
import { Segmented } from '@/components/ui/segmented';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/components/ui/use-toast';
import { AlertTriangle, BedDouble, Phone, User, Contact, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import PatientSearchInput from '@/components/shared/PatientSearchInput';
import type { PatientSearchResult } from '@/hooks/usePatientSearch';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

// ─── Select helper ─────────────────────────────────────────────────────────────
function Sel({ value, onChange, children, disabled, hasError }: {
  value: string; onChange: (v: string) => void;
  children: React.ReactNode; disabled?: boolean; hasError?: boolean;
}) {
  return (
      <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className={cn(
              "flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
              hasError
                  ? "border-destructive focus-visible:ring-destructive"
                  : "border-field focus-visible:ring-ring"
          )}
      >
        {children}
      </select>
  );
}

// ─── Form types ─────────────────────────────────────────────────────────────────
interface AdmitForm {
  patient_id: string;
  ward_id: string;
  bed_id: string;
  admitting_doctor_id: string;
  admission_date: string;
  admission_time: string;
  admission_reason: string;
  attendant_name: string;
  attendant_relation: string;
  attendant_phone: string;
  is_mlc: boolean;
  mlc_number: string;
  referred_by: string;
  rate_plan: string;
  insurance_provider: string;
  notes: string;
  deposit_amount: string;
  deposit_mode: 'cash' | 'upi' | 'card' | 'cheque';
  deposit_reference: string;
}

function emptyForm(): AdmitForm {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
  return {
    patient_id: '',
    ward_id: '', bed_id: '',
    admitting_doctor_id: '',
    admission_date: local.slice(0, 10),
    admission_time: local.slice(11, 16),
    admission_reason: '',
    attendant_name: '', attendant_relation: '', attendant_phone: '',
    is_mlc: false, mlc_number: '',
    referred_by: '',
    rate_plan: '',
    insurance_provider: '',
    notes: '',
    deposit_amount: '',
    deposit_mode: 'cash',
    deposit_reference: '',
  };
}

export interface AdmitInitialPatient {
  id: string;
  name: string;
  age?: number;
  date_of_birth?: string;
  gender?: string;
  phone?: string;
  mrn?: string;
}

interface AdmitPatientModalProps {
  open: boolean;
  onClose: () => void;
  onAdmitted: (admissionId: string) => void;
  clinicId: string;
  initialPatient?: AdmitInitialPatient;
}

export default function AdmitPatientModal({ open, onClose, onAdmitted, clinicId, initialPatient }: AdmitPatientModalProps) {
  const { currentUser } = useAuth();
  const isDoctor = currentUser?.role?.toLowerCase() === 'doctor' && !!currentUser?.doctor_id;
  const { can } = usePermission();
  const canTakeDeposit = can('billing:write');
  const { toast } = useToast();

  const [form, setForm] = useState<AdmitForm>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');

  const [wards, setWards] = useState<Ward[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [loadingBeds, setLoadingBeds] = useState(false);
  const [saving, setSaving] = useState(false);

  const [patientSearchText, setPatientSearchText] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<AdmitInitialPatient | null>(null);

  const [showAdditional, setShowAdditional] = useState(false);

  useEffect(() => {
    if (!open) return;
    const defaultDoctorId = isDoctor ? currentUser!.doctor_id! : '';
    setForm(initialPatient
        ? { ...emptyForm(), patient_id: initialPatient.id, admitting_doctor_id: defaultDoctorId }
        : { ...emptyForm(), admitting_doctor_id: defaultDoctorId });

    setSelectedPatient(initialPatient || null);
    setPatientSearchText('');
    setErrors({});
    setApiError('');
    setShowAdditional(false);
    setBeds([]);

    Promise.all([getWards(clinicId), getDoctors()]).then(([w, d]) => {
      setWards(w ?? []);
      setDoctors(d ?? []);
    });
  }, [open, clinicId, initialPatient, isDoctor, currentUser?.doctor_id]);

  useEffect(() => {
    if (!form.ward_id) { setBeds([]); return; }
    setLoadingBeds(true);
    getBeds(form.ward_id)
        .then(data => {
          const free = (data ?? []).filter(b => b.occupancy_status === 'available' && b.is_active);
          setBeds(free);
          // Only one bed free in the ward: that's the bed
          if (free.length === 1) setForm(f => (f.bed_id ? f : { ...f, bed_id: free[0].id }));
        })
        .catch(() => setBeds([]))
        .finally(() => setLoadingBeds(false));
  }, [form.ward_id]);

  const set = <K extends keyof AdmitForm>(field: K, value: AdmitForm[K]) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  };

  const handlePatientSelect = (p: PatientSearchResult) => {
    set('patient_id', p.id);
    setSelectedPatient(p as AdmitInitialPatient);
  };

  const clearPatientSelection = () => {
    setSelectedPatient(null);
    set('patient_id', '');
    setPatientSearchText('');
  };

  const handleSubmit = async () => {
    setApiError('');
    const newErrors: Record<string, string> = {};

    if (!form.patient_id) newErrors.patient_id = 'Required';
    if (!form.ward_id) newErrors.ward_id = 'Required';
    if (!form.bed_id) newErrors.bed_id = 'Required';
    if (!form.admitting_doctor_id) newErrors.admitting_doctor_id = 'Required';
    if (!form.admission_date) newErrors.admission_date = 'Required';
    if (!form.admission_time) newErrors.admission_time = 'Required';
    if (!form.admission_reason.trim()) newErrors.admission_reason = 'Required';
    if (form.attendant_phone.trim() && !/^[0-9+\-\s]{6,15}$/.test(form.attendant_phone.trim())) newErrors.attendant_phone = 'Check the number';
    const deposit = parseFloat(form.deposit_amount);
    if (form.deposit_amount && !(deposit > 0)) newErrors.deposit_amount = 'Enter an amount';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const result = await createAdmission({
        patient_id:           form.patient_id,
        ward_id:              form.ward_id,
        bed_id:               form.bed_id,
        admitting_doctor_id:  form.admitting_doctor_id,
        admission_date:       new Date(`${form.admission_date}T${form.admission_time}`).toISOString(),
        admission_reason:     form.admission_reason.trim(),
        attendant_name:       form.attendant_name.trim() || null,
        attendant_relation:   form.attendant_relation.trim() || null,
        attendant_phone:      form.attendant_phone.trim() || null,
        is_mlc:               form.is_mlc,
        mlc_number:           form.is_mlc ? form.mlc_number.trim() || null : null,
        referred_by:          form.referred_by || null,
        rate_plan:            form.rate_plan || null,
        insurance_provider:   form.insurance_provider || null,
        notes:                form.notes || null,
        deposit: canTakeDeposit && deposit > 0
          ? { amount: deposit, payment_mode: form.deposit_mode, reference_number: form.deposit_reference.trim() || undefined }
          : undefined,
      });
      if (result.deposit_error) {
        toast({ variant: 'destructive', title: 'Admitted, but the deposit was not saved', description: `${result.deposit_error} — collect it from the Bill tab.` });
      }
      onAdmitted(result.id);
    } catch (e: any) {
      setApiError(e?.message || 'Failed to admit patient. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
            className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden"
            onPointerDownOutside={(e) => {
              if ((e.target as HTMLElement)?.closest('[data-patient-search-dropdown]')) {
                e.preventDefault();
              }
            }}
        >
          {/* --- HEADER --- */}
          <DialogHeader className="px-6 py-4 border-b border-border shrink-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center gap-3 pr-6">
              <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center shrink-0">
                <BedDouble className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-0.5 text-left">
                <DialogTitle className="text-lg font-semibold text-foreground">
                  Admit patient
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Assign a bed and record admission details
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* --- SCROLLABLE BODY --- */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

            {apiError && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-4 py-3">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{apiError}
                </div>
            )}

            {/* --- BLOCK 1: PATIENT --- */}
            <div className="space-y-1.5">
              {!selectedPatient ? (
                  <>
                    <label className="block text-[13px] font-semibold text-label">Patient *</label>
                    <PatientSearchInput
                        value={patientSearchText}
                        onChange={setPatientSearchText}
                        onSelect={handlePatientSelect}
                        placeholder="Search patient by name, phone, or MRN…"
                        className={cn("h-9 bg-transparent", errors.patient_id && "border-destructive focus-visible:ring-destructive")}
                        inputSize="md"
                    />
                    {errors.patient_id && <p className="text-xs text-destructive">{errors.patient_id}</p>}
                  </>
              ) : (
                  <div className="flex flex-wrap items-center justify-between rounded-md border border-border bg-muted/40 px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {selectedPatient.name}
                        {(selectedPatient.age != null || selectedPatient.date_of_birth) && <span className="font-normal text-muted-foreground">({displayAge(selectedPatient.age, selectedPatient.date_of_birth)}, {selectedPatient.gender ?? '—'})</span>}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Phone className="w-3.5 h-3.5" />
                        <span className="font-medium text-foreground">{selectedPatient.phone || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Contact className="w-3.5 h-3.5" />
                        ID: <span className="font-medium text-foreground">{selectedPatient.mrn || '—'}</span>
                      </div>
                    </div>
                    {!initialPatient && (
                        <Button variant="ghost" size="sm" onClick={clearPatientSelection} className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground shrink-0">
                          <RefreshCw className="w-3 h-3 mr-1" /> Change
                        </Button>
                    )}
                  </div>
              )}
            </div>

            {/* --- BLOCK 2: LOCATION (Ward, Bed) --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="flex justify-between"><label className="block text-[13px] font-semibold text-label">Ward *</label> {errors.ward_id && <span className="text-xs text-destructive">{errors.ward_id}</span>}</div>
                <Sel value={form.ward_id} hasError={!!errors.ward_id} onChange={v => { set('ward_id', v); set('bed_id', ''); }}>
                  <option value="">Select ward</option>
                  {wards.filter(w => w.is_active).map(w => (
                      <option key={w.id} value={w.id} disabled={Number(w.available_beds) === 0}>{w.name} ({Number(w.available_beds) === 0 ? 'full' : `${w.available_beds} free`})</option>
                  ))}
                </Sel>
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between"><label className="block text-[13px] font-semibold text-label">Bed *</label> {errors.bed_id && <span className="text-xs text-destructive">{errors.bed_id}</span>}</div>
                <Sel value={form.bed_id} hasError={!!errors.bed_id} onChange={v => set('bed_id', v)} disabled={!form.ward_id || loadingBeds}>
                  <option value="">{!form.ward_id ? 'Select ward first' : loadingBeds ? 'Loading…' : 'Select bed'}</option>
                  {beds.map(b => (
                      <option key={b.id} value={b.id}>Bed {b.bed_number}</option>
                  ))}
                </Sel>
                {/* Rate preview — locked at admission time */}
                {form.bed_id && (() => {
                  const bed = beds.find(b => b.id === form.bed_id);
                  const rate = bed?.effective_rent_per_day ?? bed?.rent_per_day ?? null;
                  return rate != null && Number(rate) > 0 ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Room rate: <span className="font-medium text-foreground">₹{Number(rate).toLocaleString('en-IN')}/day</span> — locked at admission
                    </p>
                  ) : null;
                })()}
              </div>
            </div>

            {/* --- BLOCK 3: ADMISSION EVENT (Doctor, Date, Time) --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <div className="flex justify-between"><label className="block text-[13px] font-semibold text-label">Admitting doctor *</label> {errors.admitting_doctor_id && <span className="text-xs text-destructive">{errors.admitting_doctor_id}</span>}</div>
                <Sel value={form.admitting_doctor_id} hasError={!!errors.admitting_doctor_id} onChange={v => set('admitting_doctor_id', v)}>
                  <option value="">Select doctor</option>
                  {doctors.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Sel>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between"><label className="block text-[13px] font-semibold text-label">Admission date *</label> {errors.admission_date && <span className="text-xs text-destructive">{errors.admission_date}</span>}</div>
                <Input type="date" value={form.admission_date} onChange={e => set('admission_date', e.target.value)} className={cn("h-9 text-sm bg-transparent", errors.admission_date && "border-destructive focus-visible:ring-destructive")} />
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between"><label className="block text-[13px] font-semibold text-label">Admission time *</label> {errors.admission_time && <span className="text-xs text-destructive">{errors.admission_time}</span>}</div>
                <Input type="time" value={form.admission_time} onChange={e => set('admission_time', e.target.value)} className={cn("h-9 text-sm bg-transparent", errors.admission_time && "border-destructive focus-visible:ring-destructive")} />
              </div>
            </div>

            {/* --- BLOCK 3b: WHY, WHO IS WITH THE PATIENT, MEDICO-LEGAL --- */}
            <div className="space-y-1.5">
              <div className="flex justify-between"><label htmlFor="admit-reason" className="block text-[13px] font-semibold text-label">Reason for admission / provisional diagnosis *</label> {errors.admission_reason && <span className="text-xs text-destructive">{errors.admission_reason}</span>}</div>
              <Input id="admit-reason" value={form.admission_reason} maxLength={500} onChange={e => set('admission_reason', e.target.value)}
                placeholder="e.g. Dengue fever with thrombocytopenia"
                className={cn("h-9 text-sm bg-transparent", errors.admission_reason && "border-destructive focus-visible:ring-destructive")} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="admit-attendant" className="block text-[13px] font-semibold text-label">Attendant</label>
                <Input id="admit-attendant" value={form.attendant_name} maxLength={100} onChange={e => set('attendant_name', e.target.value)} className="h-9 text-sm bg-transparent" placeholder="Name" />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="admit-relation" className="block text-[13px] font-semibold text-label">Relation</label>
                <Input id="admit-relation" value={form.attendant_relation} maxLength={50} onChange={e => set('attendant_relation', e.target.value)} className="h-9 text-sm bg-transparent" placeholder="e.g. Son, Wife" />
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between"><label htmlFor="admit-attendant-phone" className="block text-[13px] font-semibold text-label">Attendant's phone</label> {errors.attendant_phone && <span className="text-xs text-destructive">{errors.attendant_phone}</span>}</div>
                <Input id="admit-attendant-phone" type="tel" inputMode="tel" value={form.attendant_phone} maxLength={15} onChange={e => set('attendant_phone', e.target.value)}
                  className={cn("h-9 text-sm bg-transparent", errors.attendant_phone && "border-destructive focus-visible:ring-destructive")} />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4 accent-primary" checked={form.is_mlc} onChange={e => set('is_mlc', e.target.checked)} />
                Medico-legal case (MLC)
              </label>
              {form.is_mlc && (
                <Input aria-label="MLC number" value={form.mlc_number} maxLength={50} onChange={e => set('mlc_number', e.target.value)}
                  className="h-9 w-48 text-sm bg-transparent" placeholder="MLC / police intimation no." />
              )}
            </div>

            {/* --- DEPOSIT (optional): held as the patient's advance for the final bill --- */}
            {canTakeDeposit && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <div className="flex justify-between"><label htmlFor="admit-deposit" className="block text-[13px] font-semibold text-label">Deposit (₹)</label> {errors.deposit_amount && <span className="text-xs text-destructive">{errors.deposit_amount}</span>}</div>
                  <Input id="admit-deposit" type="number" inputMode="decimal" min="0" step="0.01" placeholder="Optional"
                    value={form.deposit_amount} onChange={e => set('deposit_amount', e.target.value)}
                    className={cn("h-9 text-sm font-mono bg-transparent", errors.deposit_amount && "border-destructive focus-visible:ring-destructive")} />
                </div>
                {parseFloat(form.deposit_amount) > 0 && (
                  <>
                    <div className="space-y-1.5">
                      <span className="block text-[13px] font-semibold text-label">Mode</span>
                      <Segmented aria-label="Deposit mode" value={form.deposit_mode} className="w-full h-9"
                        onChange={v => { set('deposit_mode', v); set('deposit_reference', ''); }}
                        options={[{ value: 'cash', label: 'Cash' }, { value: 'upi', label: 'UPI' }, { value: 'card', label: 'Card' }, { value: 'cheque', label: 'Cheque' }]} />
                    </div>
                    {form.deposit_mode !== 'cash' && (
                      <div className="space-y-1.5">
                        <label htmlFor="admit-deposit-ref" className="block text-[13px] font-semibold text-label">{form.deposit_mode === 'cheque' ? 'Cheque number' : 'Transaction ID'}</label>
                        <Input id="admit-deposit-ref" value={form.deposit_reference} onChange={e => set('deposit_reference', e.target.value)} className="h-9 text-sm bg-transparent" />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <Separator />

            {/* --- BLOCK 4: ADMINISTRATIVE DETAILS --- */}
            <div>
              <button
                  type="button"
                  onClick={() => setShowAdditional(!showAdditional)}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left"
              >
                {showAdditional ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showAdditional ? 'Hide' : 'Add'} insurance and referral details
              </button>

              {showAdditional && (
                  <div className="space-y-4 pt-5 animate-in fade-in slide-in-from-top-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <label className="block text-[13px] font-semibold text-label">Referring doctor</label>
                        <Input value={form.referred_by} onChange={e => set('referred_by', e.target.value)} className="h-9 text-sm bg-transparent" placeholder="e.g. Dr. A. Patil" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="block text-[13px] font-semibold text-label">Billing tariff / rate</label>
                        <Input value={form.rate_plan} onChange={e => set('rate_plan', e.target.value)} className="h-9 text-sm bg-transparent" placeholder="e.g. Standard, Corporate" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="block text-[13px] font-semibold text-label">Insurance / TPA</label>
                        <Input value={form.insurance_provider} onChange={e => set('insurance_provider', e.target.value)} className="h-9 text-sm bg-transparent" placeholder="e.g. HDFC Ergo" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-[13px] font-semibold text-label">Admission notes</label>
                      <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="text-sm bg-transparent min-h-[60px]" placeholder="Special instructions, dietary needs, or handover notes..." />
                    </div>
                  </div>
              )}
            </div>
          </div>

          {/* --- FOOTER --- */}
          <DialogFooter className="px-6 py-4 border-t border-border shrink-0 bg-background">
            <Button variant="outline" onClick={onClose} disabled={saving} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-auto">{saving ? 'Admitting…' : 'Admit patient'}</Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>
  );
}